/**
 * "Import from board": pick one of the current board's groups to capture into the
 * editor. Lists the groups with their colours, offers to include updates as
 * plain text, and hands the choice back; the editor does the reading.
 */
import { Checkbox, Modal, ModalBasicLayout, ModalContent, ModalFooter, ModalHeader } from '@vibe/core';
import React, { useEffect, useState } from 'react';
import { fetchGroups } from '../../lib/captureGroup';
import Spinner from '../Spinner';

export default function GroupPicker({ open, api, boardId, capturing, onCapture, onClose }) {
  const [groups, setGroups] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [includeUpdates, setIncludeUpdates] = useState(true);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setGroups(null);
    setError(null);
    fetchGroups(api, boardId)
      .then((list) => alive && setGroups(list))
      .catch((e) => alive && setError((e && e.message) || 'request failed'));
    return () => {
      alive = false;
    };
  }, [open, api, boardId]);

  return (
    <Modal id="spt-group-picker" show={open} onClose={onClose} size="medium">
      <ModalBasicLayout>
        <ModalHeader title="Import a group from this board" />
        <ModalContent>
          <p className="mb-3 text-sm text-secondary">
            The group's items, their subitems and values become this template's rows. Nothing is saved until you
            click Save template.
          </p>
          {groups === null && !error && (
            <div className="inline-flex items-center gap-2 text-sm text-secondary" role="status">
              <Spinner />
              Reading the board's groups
            </div>
          )}
          {error && (
            <div className="text-sm text-primary" role="alert">
              Could not read the board's groups. monday said: “{error}”.
            </div>
          )}
          {groups && !groups.length && <div className="text-sm text-secondary">This board has no groups.</div>}
          {groups && groups.length > 0 && (
            <div className="flex flex-col gap-1 max-h-72 overflow-y-auto" role="radiogroup" aria-label="Groups">
              {groups.map((g) => (
                <label key={g.id} className="flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-hover">
                  <input type="radio" name="spt-group-pick" checked={groupId === g.id} onChange={() => setGroupId(g.id)} />
                  <span className="w-3 h-3 rounded-full flex-none" style={{ backgroundColor: g.color }} aria-hidden="true" />
                  <span className="text-primary">{g.title}</span>
                </label>
              ))}
            </div>
          )}
          <div className="mt-4">
            <Checkbox
              className="spt-checkbox"
              checkboxClassName="spt-checkbox-box"
              label="Include updates (text and mentions, no files)"
              checked={includeUpdates}
              onChange={(e) => setIncludeUpdates(Boolean(e.target.checked))}
            />
          </div>
        </ModalContent>
      </ModalBasicLayout>
      <ModalFooter
        primaryButton={{
          text: 'Import',
          disabled: !groupId || capturing,
          loading: capturing,
          onClick: () => onCapture({ groupId, includeUpdates }),
        }}
        secondaryButton={{ text: 'Cancel', disabled: capturing, onClick: onClose }}
      />
    </Modal>
  );
}
